/**
 * © 2025 MyDebugger Contributors – MIT License
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { TOOL_PANEL_CLASS } from "@design-system";
import { Helmet } from "react-helmet";
import QRCodeStyling, {
  DotType,
  CornerSquareType,
  ErrorCorrectionLevel,
} from "qr-code-styling";
import { QR_PRESETS, getPresetByName } from "./lib/qrcodePresets";
import { useLocation } from "react-router-dom";
import { generateICalEvent } from "./lib/ical";
import { useTranslation } from "../../context/TranslationContext";
import { copyText } from "../../shared/utils/clipboard";
import { renderQrCard } from "./lib/qrImage";

// Interface definitions for saved QR codes
interface SavedQRCode {
  id: string;
  url: string;
  nickname: string;
  createdAt: number;
  qrCodeUrl: string;
  settings: {
    size: number;
    errorCorrection: ErrorCorrectionLevel;
    darkColor: string;
    lightColor: string;
  };
}

interface SelectionContextMenu {
  x: number;
  y: number;
  start: number;
  end: number;
}

const DeepLinkQRGenerator: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialLink = searchParams.get("link") || "";

  const [input, setInput] = useState<string>(initialLink);
  const [size, setSize] = useState<number>(256);
  const [errorCorrection, setErrorCorrection] = useState<ErrorCorrectionLevel>(
    "M",
  );
  const [darkColor, setDarkColor] = useState<string>("#000000");
  const [lightColor, setLightColor] = useState<string>("#FFFFFF");
  const [dotStyle, setDotStyle] = useState<DotType>("square");
  const [eyeStyle, setEyeStyle] = useState<CornerSquareType>("square");
  const [gradientStart, setGradientStart] = useState<string>("#000000");
  const [gradientEnd, setGradientEnd] = useState<string>("#000000");
  const [gradientAngle, setGradientAngle] = useState<number>(0);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [logoSize, setLogoSize] = useState<number>(20); // percent
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>("");
  // i18n
  const { t, language } = useTranslation();
  const [isRunningLink, setIsRunningLink] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [showCosmeticOptions, setShowCosmeticOptions] =
    useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [mobileOS, setMobileOS] = useState<string>("");
  const [contextMenu, setContextMenu] = useState<SelectionContextMenu | null>(null);

  // QR type state
  const [qrType, setQrType] = useState<string>('link');
  const [wifiSsid, setWifiSsid] = useState('');
  const [wifiPassword, setWifiPassword] = useState('');
  const [wifiEncryption, setWifiEncryption] = useState('WPA');
  const [geoLat, setGeoLat] = useState('');
  const [geoLng, setGeoLng] = useState('');

  // Calendar state
  const [calTitle, setCalTitle] = useState('');
  const [calLocation, setCalLocation] = useState('');
  const [calDescription, setCalDescription] = useState('');
  const [calStart, setCalStart] = useState('');
  const [calEnd, setCalEnd] = useState('');
  const [calTimezone, setCalTimezone] = useState('UTC');
  const [calUid, setCalUid] = useState('');
  const [calAlarm, setCalAlarm] = useState('None');
  const [icalText, setIcalText] = useState('');


  // Collection related state
  const [savedQRCodes, setSavedQRCodes] = useState<SavedQRCode[]>([]);

  const qrRef = useRef<HTMLDivElement>(null);
  const qrInstanceRef = useRef<QRCodeStyling | null>(null);
  const qrUrlRef = useRef<string | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const getQRData = useCallback(() => {
    switch (qrType) {
      case 'text':
        return input;
      case 'phone':
        return `tel:${input}`;
      case 'wifi':
        return `WIFI:T:${wifiEncryption};S:${wifiSsid};P:${wifiPassword};;`;
      case 'geo':
        return `geo:${geoLat},${geoLng}`;
      case 'calendar':
        return icalText;
      default:
        return input;
    }
  }, [
    qrType,
    input,
    wifiEncryption,
    wifiSsid,
    wifiPassword,
    geoLat,
    geoLng,
    icalText,
  ]);

  const handleInputContextMenu = (
    event: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { currentTarget } = event;
    const start = currentTarget.selectionStart ?? 0;
    const end = currentTarget.selectionEnd ?? 0;
    if (start === end) return;

    event.preventDefault();
    setContextMenu({ x: event.clientX, y: event.clientY, start, end });
  };

  const handleSelectedTextTransform = (mode: 'encode' | 'decode') => {
    if (!contextMenu) return;

    try {
      const selected = input.slice(contextMenu.start, contextMenu.end);
      const transformed = mode === 'encode'
        ? encodeURIComponent(selected)
        : decodeURIComponent(selected);
      setInput(
        `${input.slice(0, contextMenu.start)}${transformed}${input.slice(contextMenu.end)}`,
      );
    } catch {
      setToastMessage(
        mode === 'decode'
          ? 'Selected text is not valid encoded text.'
          : 'Selected text could not be encoded.',
      );
    } finally {
      setContextMenu(null);
    }
  };

  useEffect(() => {
    if (!contextMenu) return;

    const closeContextMenu = () => setContextMenu(null);
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeContextMenu();
    };

    document.addEventListener('click', closeContextMenu);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('click', closeContextMenu);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [contextMenu]);

  // Check if device is mobile and load saved QR codes
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor;
      const isMobileDevice =
        /android|webos|iphone|ipad|ipod|blackberry|windows phone/i.test(
          userAgent,
        );
      setIsMobile(isMobileDevice);
      if (/android/i.test(userAgent)) setMobileOS("Android");
      else if (/iphone|ipad|ipod/i.test(userAgent)) setMobileOS("iPhone");
      else setMobileOS("");
    };

    checkMobile();

    // Load saved cosmetic options
    const savedOptions = localStorage.getItem("qrCosmeticOptions");
    if (savedOptions) {
      try {
        const options = JSON.parse(savedOptions);
        setSize(options.size || 256);
        setErrorCorrection(options.errorCorrection || "M");
        setDarkColor(options.darkColor || "#000000");
        setLightColor(options.lightColor || "#FFFFFF");
        setDotStyle(options.dotStyle || "square");
        setEyeStyle(options.eyeStyle || "square");
        setGradientStart(options.gradientStart || "#000000");
        setGradientEnd(options.gradientEnd || "#000000");
        setGradientAngle(options.gradientAngle || 0);
        setLogoSize(options.logoSize || 20);
        setShowCosmeticOptions(options.showCosmeticOptions || false);
      } catch (error) {
        console.error("Error loading saved options:", error);
      }
    }

    // Load saved QR codes from localStorage
    const savedCodes = localStorage.getItem("savedQRCodes");
    if (savedCodes) {
      try {
        setSavedQRCodes(JSON.parse(savedCodes));
      } catch (error) {
        console.error("Error loading saved QR codes:", error);
      }
    }

    // Handle URL query parameter for link
    if (initialLink) {
      try {
        // Ensure the link has a valid protocol by trying to parse it
        try {
          new URL(initialLink);
          setInput(initialLink);
        } catch {
          // If no protocol, add https:// prefix
          setInput(`https://${initialLink}`);
        }
      } catch (error) {
        console.error("Invalid URL:", error);
        setInput(initialLink); // Use as-is if decoding fails
      }
    }
  }, [initialLink]);

  // Save cosmetic options to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(
        "qrCosmeticOptions",
        JSON.stringify({
          size,
          errorCorrection,
          darkColor,
          lightColor,
          dotStyle,
          eyeStyle,
          gradientStart,
          gradientEnd,
          gradientAngle,
          logoSize,
          showCosmeticOptions,
        }),
      );
    } catch (error) {
      console.error("Error saving options:", error);
    }
  }, [
    size,
    errorCorrection,
    darkColor,
    lightColor,
    dotStyle,
    eyeStyle,
    gradientStart,
    gradientEnd,
    gradientAngle,
    logoSize,
    showCosmeticOptions,
  ]);

  // Build iCal text when calendar fields change
  useEffect(() => {
    if (qrType !== 'calendar') return;
    let uid = calUid;
    if (!uid) {
      uid = typeof crypto?.randomUUID === 'function' ? crypto.randomUUID() : Date.now().toString();
      setCalUid(uid);
    }
    try {
      const alarmMap: Record<string, number | undefined> = {
        None: undefined,
        '5 minutes before': 5,
        '15 minutes before': 15,
        '1 hour before': 60,
      };
      const txt = generateICalEvent({
        summary: calTitle,
        location: calLocation,
        description: calDescription,
        start: calStart,
        end: calEnd,
        timezone: calTimezone,
        uid,
        alarmMinutes: alarmMap[calAlarm],
      });
      setIcalText(txt);
    } catch {
      setIcalText('');
    }
  }, [
    qrType,
    calTitle,
    calLocation,
    calDescription,
    calStart,
    calEnd,
    calTimezone,
    calUid,
    calAlarm,
  ]);

  // Generate QR code when input or properties change
  useEffect(() => {
    const data = getQRData();

    if (data) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        generateQRCode();
      }, 300) as unknown as number;
    } else {
      setQrCodeUrl('');
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [
    input,
    qrType,
    wifiSsid,
    wifiPassword,
    wifiEncryption,
    geoLat,
    geoLng,
    size,
    errorCorrection,
    darkColor,
    lightColor,
    dotStyle,
    eyeStyle,
    gradientStart,
    gradientEnd,
    gradientAngle,
    logoDataUrl,
    logoSize,
    icalText,
  ]);


  // Clear toast message after 2 seconds
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(""), 2000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  // Show helper message if deeplink taking too long
  useEffect(() => {
    if (isRunningLink) {
      const timer = setTimeout(() => {
        setToastMessage(
          "If nothing happens, the app may not be installed or this link type isn't supported on your device.",
        );
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isRunningLink]);

  const generateQRCode = async () => {
    if (!qrInstanceRef.current) {
      qrInstanceRef.current = new QRCodeStyling({ type: "canvas" });
    }

    const linkForQR = getQRData();
    const gradient =
      gradientStart !== gradientEnd
        ? {
            type: "linear" as const,
            rotation: (gradientAngle * Math.PI) / 180,
            colorStops: [
              { offset: 0, color: gradientStart },
              { offset: 1, color: gradientEnd },
            ],
          }
        : undefined;

    qrInstanceRef.current.update({
      width: size,
      height: size,
      data: linkForQR,
      qrOptions: { errorCorrectionLevel: errorCorrection },
      dotsOptions: {
        type: dotStyle,
        color: gradient ? undefined : darkColor,
        gradient,
      },
      cornersSquareOptions: { type: eyeStyle, color: darkColor },
      cornersDotOptions: { type: eyeStyle, color: darkColor },
      backgroundOptions: { color: lightColor },
      image: logoDataUrl || undefined,
      imageOptions: { crossOrigin: "anonymous", imageSize: logoSize / 100 },
    });

    if (qrRef.current) {
      qrRef.current.innerHTML = "";
      qrInstanceRef.current.append(qrRef.current);
    }

    const blob = (await qrInstanceRef.current.getRawData("png")) as Blob;
    if (blob) {
      if (qrUrlRef.current) {
        URL.revokeObjectURL(qrUrlRef.current);
      }
      qrUrlRef.current = URL.createObjectURL(blob);
      setQrCodeUrl(qrUrlRef.current);
    }
  };

  const copyToClipboard = useCallback(async (text: string, message: string) => {
    const copied = await copyText(text);
    setToastMessage(
      copied
        ? message
        : "Clipboard access denied. Please update your browser permissions.",
    );
  }, []);

  const handleCopyImage = async () => {
    if (!qrCodeUrl) return;
    if (typeof ClipboardItem === "undefined" || !navigator.clipboard?.write) {
      setToastMessage("Copying images isn't supported in this browser. Use download instead.");
      return;
    }
    try {
      // Pass the promise (not the resolved blob) so Safari keeps the user-gesture context.
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": renderQrCard(qrCodeUrl, input) }),
      ]);
      setToastMessage("QR image copied!");
    } catch (error) {
      console.error("Error copying QR image:", error);
      setToastMessage("Clipboard access denied. Please update your browser permissions.");
    }
  };

  const handleLogoUpload =(e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setLogoDataUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRunLink = () => {
    if (qrType !== 'link' || !input) return;

    setIsRunningLink(true);
    setTimeout(() => {
      try {
        window.location.href = input;
      } catch (error) {
        console.error("Error opening link:", error);
        setToastMessage("Error opening link");
      }
      setTimeout(() => setIsRunningLink(false), 1000);
    }, 10);
  };

  const handleSharePageWithLink = () => {
    if (qrType !== 'link' || !input) return;

    try {
      // Create a URL object for the current page
      const currentUrl = new URL(window.location.href);

      // Reset any existing search parameters
      currentUrl.search = "";

      // URLSearchParams handles encoding for the share URL without changing the link itself.
      currentUrl.searchParams.set("link", input);

      // Copy the full URL to clipboard
      copyToClipboard(
        currentUrl.toString(),
        "Shareable link copied! Send to your team.",
      );
    } catch (error) {
      console.error("Error creating shareable link:", error);
      setToastMessage("Error creating shareable link");
    }
  };

  // removed legacy style preset dropdown in favor of a single, modern preset selector

  // Load QR code from collection
  const handleLoadFromCollection = (saved: SavedQRCode) => {
    setInput(saved.url);
    setQrType('link');
    setSize(saved.settings.size);
    setErrorCorrection(saved.settings.errorCorrection);
    setDarkColor(saved.settings.darkColor);
    setLightColor(saved.settings.lightColor);
    setQrCodeUrl(saved.qrCodeUrl);
  };

  // Remove QR code from collection
  const handleRemoveFromCollection = (id: string) => {
    const updatedCollection = savedQRCodes.filter((code) => code.id !== id);
    setSavedQRCodes(updatedCollection);

    // Save to localStorage
    try {
      localStorage.setItem("savedQRCodes", JSON.stringify(updatedCollection));
      setToastMessage("Removed from collection");
    } catch (error) {
      console.error("Error removing from collection:", error);
    }
  };

  // SEO metadata
  const pageDescription = t(
    "tools.qrcode-generator.description",
    "Generate QR codes for links & deeplinks, test them directly and share with your team."
  );

  return (
    <>
      <Helmet>
        <title>{t('tools.qrcode-generator.title', 'Deep-Link Tester & QR Generator')} – iOS & Android</title>
        <meta
          name="description"
          content={t('qrcode.meta.description', 'Test deep links instantly and generate beautiful, share-ready QR codes with gradients and logos. Works with custom schemes like myapp:// and https:// URLs.')}
        />
        <meta name="keywords" content="qr, qr code, generator, deeplink, url, ios, android, gradient qr, logo qr, svg, pdf" />
        <meta
          property="og:title"
          content={`${t('tools.qrcode-generator.title', 'Deep-Link Tester & QR Generator')} – MyDebugger`}
        />
        <meta
          property="og:description"
          content={t('qrcode.meta.og', 'Create deep link QR codes and run links on devices. Built for modern teams.')}
        />
        <meta property="og:image" content="/og-deeplink-preview.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://mydebugger.vercel.app/qrcode" />
        <link rel="alternate" href="https://mydebugger.vercel.app/qrcode" hrefLang="en" />
        <link rel="alternate" href="https://mydebugger.vercel.app/qrcode" hrefLang="th" />
        <meta property="og:locale" content={language === 'th' ? 'th_TH' : 'en_US'} />
        <meta property="og:locale:alternate" content={language === 'th' ? 'en_US' : 'th_TH'} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": ["WebApplication", "SoftwareApplication"],
            name: t('tools.qrcode-generator.title', 'Deep-Link Tester & QR Generator'),
            url: "https://mydebugger.vercel.app/qrcode",
            applicationCategory: "DeveloperTool",
            description: pageDescription,
            creator: { "@type": "Person", name: "Jirad Sreerattana-arporn" },
            offers: { "@type": "Offer", price: 0, priceCurrency: "USD" },
            operatingSystem: "Web"
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: t('qrcode.howto.title', 'How to generate a QR code'),
            step: [
              { "@type": "HowToStep", name: t('qrcode.howto.step1', 'Enter a URL or choose a type') },
              { "@type": "HowToStep", name: t('qrcode.howto.step2', 'Pick a style preset or colors') },
              { "@type": "HowToStep", name: t('qrcode.howto.step3', 'Download as PNG, SVG or PDF') }
            ]
          })}
        </script>
      </Helmet>

      <div className="container mx-auto px-4 py-5 sm:py-6">
        <p className="text-sm text-gray-600 mb-4">{t('qrcode.lede', 'Generate QR codes for any URL or deeplink, preview safe links, and test instantly on your device.')}</p>

        {/* Toast Message */}
        {toastMessage && (
          <div className="fixed top-20 right-4 z-50 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg animate-fade-in-out">
            {toastMessage}
          </div>
        )}

        {contextMenu && (
          <div
            role="menu"
            aria-label="Text actions"
            tabIndex={-1}
            className="fixed z-[60] min-w-44 overflow-hidden rounded-lg border border-gray-200 bg-white p-1 shadow-xl"
            style={{
              left: `${Math.max(8, Math.min(contextMenu.x, window.innerWidth - 184))}px`,
              top: `${Math.max(8, Math.min(contextMenu.y, window.innerHeight - 88))}px`,
            }}
          >
            <button
              type="button"
              role="menuitem"
              className="block w-full rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => handleSelectedTextTransform('encode')}
            >
              Encode selected
            </button>
            <button
              type="button"
              role="menuitem"
              className="block w-full rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => handleSelectedTextTransform('decode')}
            >
              Decode selected
            </button>
          </div>
        )}

        <div className={`${TOOL_PANEL_CLASS.replace("p-6", "p-4")} flex flex-col gap-4 mb-5`}>
          {/* Input Section */}
          <div className="order-2">
            <div>
              <label htmlFor="qrType" className="block font-medium text-gray-700 mb-1">
                {t('qrcode.type', 'QR Code Type')}
              </label>
              <select
                id="qrType"
                className="w-full rounded-md border-gray-300 mb-3"
                value={qrType}
                onChange={(e) => setQrType(e.target.value)}
              >
                <option value="link">{t('qrcode.types.link', 'Link')}</option>
                <option value="text">{t('qrcode.types.text', 'Text')}</option>
                <option value="phone">{t('qrcode.types.phone', 'Phone')}</option>
                <option value="wifi">{t('qrcode.types.wifi', 'Wi‑Fi')}</option>
                <option value="geo">{t('qrcode.types.geo', 'Geo')}</option>
                <option value="calendar">{t('qrcode.types.calendar', 'Calendar')}</option>
              </select>

              {qrType === 'link' && (
                <>
                  <label htmlFor="input" className="block font-medium text-gray-700 mb-1">
                    {t('qrcode.urlOrDeeplink', 'URL or Deeplink')}
                  </label>
                  <textarea
                    id="input"
                    rows={4}
                    className="w-full min-h-32 resize-y rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 mb-2 font-mono text-sm leading-6"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onContextMenu={handleInputContextMenu}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleRunLink();
                      }
                    }}
                    placeholder="https://example.com/"
                    autoFocus
                  />
                </>
              )}

              {qrType === 'text' && (
                <>
                  <label htmlFor="input" className="block font-medium text-gray-700 mb-1">
                    Text
                  </label>
                  <textarea
                    id="input"
                    rows={3}
                    className="w-full rounded-md border-gray-300 shadow-sm mb-2"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Just some text here."
                  />
                </>
              )}

              {qrType === 'phone' && (
                <>
                  <label htmlFor="input" className="block font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="input"
                    className="w-full rounded-md border-gray-300 shadow-sm mb-2"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="+66812345678"
                  />
                </>
              )}

              {qrType === 'wifi' && (
                <>
                  <label className="block font-medium text-gray-700 mb-1">SSID</label>
                  <input
                    type="text"
                    className="w-full rounded-md border-gray-300 shadow-sm mb-2"
                    value={wifiSsid}
                    onChange={(e) => setWifiSsid(e.target.value)}
                    placeholder="MyWiFi"
                  />
                  <label className="block font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="text"
                    className="w-full rounded-md border-gray-300 shadow-sm mb-2"
                    value={wifiPassword}
                    onChange={(e) => setWifiPassword(e.target.value)}
                  />
                  <label className="block font-medium text-gray-700 mb-1">Encryption</label>
                  <select
                    className="w-full rounded-md border-gray-300 mb-2"
                    value={wifiEncryption}
                    onChange={(e) => setWifiEncryption(e.target.value)}
                  >
                    <option value="WPA">WPA/WPA2</option>
                    <option value="WEP">WEP</option>
                    <option value="nopass">None</option>
                  </select>
                </>
              )}

              {qrType === 'geo' && (
                <>
                  <label className="block font-medium text-gray-700 mb-1">Latitude</label>
                  <input
                    type="text"
                    className="w-full rounded-md border-gray-300 shadow-sm mb-2"
                    value={geoLat}
                    onChange={(e) => setGeoLat(e.target.value)}
                    placeholder="13.7563"
                  />
                  <label className="block font-medium text-gray-700 mb-1">Longitude</label>
                  <input
                    type="text"
                    className="w-full rounded-md border-gray-300 shadow-sm mb-2"
                    value={geoLng}
                    onChange={(e) => setGeoLng(e.target.value)}
                    placeholder="100.5018"
                  />
                </>
              )}

              {qrType === 'calendar' && (
                <>
                  <label className="block font-medium text-gray-700 mb-1">Event Title</label>
                  <input
                    type="text"
                    className="w-full rounded-md border-gray-300 shadow-sm mb-2"
                    value={calTitle}
                    onChange={(e) => setCalTitle(e.target.value)}
                    placeholder="Team Meeting"
                  />
                  <label className="block font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    className="w-full rounded-md border-gray-300 shadow-sm mb-2"
                    value={calLocation}
                    onChange={(e) => setCalLocation(e.target.value)}
                    placeholder="Room 5B"
                  />
                  <label className="block font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    rows={2}
                    className="w-full rounded-md border-gray-300 shadow-sm mb-2"
                    value={calDescription}
                    onChange={(e) => setCalDescription(e.target.value)}
                    placeholder="Meeting agenda and notes"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block font-medium text-gray-700 mb-1">Start Date &amp; Time</label>
                      <input
                        type="datetime-local"
                        className="w-full rounded-md border-gray-300 shadow-sm mb-2"
                        value={calStart}
                        onChange={(e) => setCalStart(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block font-medium text-gray-700 mb-1">End Date &amp; Time</label>
                      <input
                        type="datetime-local"
                        className="w-full rounded-md border-gray-300 shadow-sm mb-2"
                        value={calEnd}
                        onChange={(e) => setCalEnd(e.target.value)}
                      />
                    </div>
                  </div>
                  <label className="block font-medium text-gray-700 mb-1">Timezone</label>
                  <select
                    className="w-full rounded-md border-gray-300 mb-2"
                    value={calTimezone}
                    onChange={(e) => setCalTimezone(e.target.value)}
                  >
                    <option value="UTC">UTC</option>
                    <option value="Asia/Bangkok">Asia/Bangkok</option>
                    <option value="America/New_York">America/New_York</option>
                  </select>
                  <label className="block font-medium text-gray-700 mb-1">UID (optional)</label>
                  <input
                    type="text"
                    className="w-full rounded-md border-gray-300 shadow-sm mb-2"
                    value={calUid}
                    onChange={(e) => setCalUid(e.target.value)}
                    placeholder="auto-generated if blank"
                  />
                  <label className="block font-medium text-gray-700 mb-1">Alarm Reminder</label>
                  <select
                    className="w-full rounded-md border-gray-300 mb-2"
                    value={calAlarm}
                    onChange={(e) => setCalAlarm(e.target.value)}
                  >
                    <option>None</option>
                    <option>5 minutes before</option>
                    <option>15 minutes before</option>
                    <option>1 hour before</option>
                  </select>
                  <div className="mt-3">
                    <h3 className="font-semibold mb-1">iCal Output</h3>
                    <textarea
                      readOnly
                      value={icalText}
                      rows={6}
                      className="w-full rounded-md border-gray-300 shadow-sm"
                    />
                    <div className="flex gap-2 mt-2">
                      <button
                        type="button"
                        className="px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300"
                        onClick={() => copyToClipboard(icalText, 'iCal copied!')}
                      >
                        📋 Copy
                      </button>
                      <button
                        type="button"
                        className="px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300"
                        onClick={() => {
                          const blob = new Blob([icalText], { type: 'text/calendar' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = 'event.ics';
                          link.click();
                          URL.revokeObjectURL(url);
                        }}
                      >
                        💾 Download .ics
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                {qrType === 'link' && (
                  <button
                    onClick={handleSharePageWithLink}
                    disabled={!input}
                    className={`flex items-center justify-center px-3 py-1.5 text-sm rounded-md text-white transition ${
                      input
                        ? "bg-indigo-500 hover:bg-indigo-600"
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
                  >
                    <svg
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                      />
                    </svg>
                    {t('qrcode.buttons.sharePage', 'Share Page with Link Pre‑filled')}
                  </button>
                )}
                {qrType === 'link' && (
                  <button
                    onClick={handleRunLink}
                    disabled={!input}
                    className={`flex items-center justify-center px-3 py-1.5 text-sm rounded-md text-white transition ${
                      input
                        ? "bg-green-500 hover:bg-green-600"
                        : "bg-gray-300 cursor-not-allowed"
                    }`}
                  >
                    <svg
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {isRunningLink
                      ? t('qrcode.buttons.opening', 'Opening...')
                      : isMobile
                        ? `${t('qrcode.buttons.openOn', 'Open on')} ${mobileOS || t('qrcode.buttons.device','Device')}`
                        : t('qrcode.buttons.runOnThisDevice', 'Run on This Device')}
                  </button>
                )}
              </div>

              {/* Collapsible Cosmetic Options */}
              <details
                className="mt-4 group"
                open={showCosmeticOptions}
              >
                <summary
                  className="summary-no-marker flex items-center justify-between p-2.5 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition text-sm font-medium text-gray-700"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowCosmeticOptions(!showCosmeticOptions);
                  }}
                >
                  <span className="flex items-center">
                    <svg
                      className={`h-5 w-5 mr-2 transition-transform ${showCosmeticOptions ? "transform rotate-90" : ""}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                    {t('qrcode.advanced', 'Advanced options')}
                  </span>
                </summary>

                <div className="pt-3 rounded-xl border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-800 shadow-sm">
                  <div className="mb-3">
                    <label
                      htmlFor="preset-main"
                      className="block text-sm font-semibold text-gray-800 dark:text-gray-100 mb-1"
                    >
                      {t('qrcode.stylePreset', 'Style Preset')}
                    </label>
                    <select
                      id="preset-main"
                      className="w-full h-10 text-sm rounded-lg border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                      value={selectedPreset || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedPreset(val || null);
                        const preset = getPresetByName(val);
                        if (preset) {
                          setDarkColor(preset.darkColor);
                          setLightColor(preset.lightColor);
                          setDotStyle(preset.dotStyle);
                          setEyeStyle(preset.eyeStyle);
                          setGradientStart(preset.gradient?.start || preset.darkColor);
                          setGradientEnd(preset.gradient?.end || preset.darkColor);
                          setGradientAngle(preset.gradient?.angle || 0);
                        }
                      }}
                    >
                      <option value="">{t('qrcode.custom', 'Custom...')}</option>
                      {QR_PRESETS.map((p) => (
                        <option key={p.name} value={p.name}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                  {/* Legacy preset selector removed. The main preset selector below is now the primary control. */}

                  <div>
                    <label
                      htmlFor="size"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Size (px)
                    </label>
                    <div className="flex items-center">
                      <input
                        type="range"
                        id="size"
                        className="flex-1 mr-3 accent-blue-500"
                        min="128"
                        max="512"
                        step="8"
                        value={size}
                        onChange={(e) => setSize(parseInt(e.target.value, 10))}
                      />
                      <div className="text-sm text-gray-500 w-12 text-right">
                        {size}px
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="errorCorrection"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        {t('qrcode.errorCorrection', 'Error Correction')}
                      </label>
                      <select
                        id="errorCorrection"
                        className="w-full rounded-md border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                        value={errorCorrection}
                        onChange={(e) =>
                          setErrorCorrection(e.target.value as ErrorCorrectionLevel)
                        }
                      >
                        <option value="L">{t('qrcode.ec.low', 'Low (7%)')}</option>
                        <option value="M">{t('qrcode.ec.medium', 'Medium (15%)')}</option>
                        <option value="Q">{t('qrcode.ec.quartile', 'Quartile (25%)')}</option>
                        <option value="H">{t('qrcode.ec.high', 'High (30%)')}</option>
                      </select>
                    </div>
                    
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="darkColor"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        {t('qrcode.darkColor', 'Dark Color')}
                      </label>
                      <div className="flex items-center">
                        <input
                          type="color"
                          id="darkColor"
                          className="h-9 w-9 rounded-md mr-2 border border-gray-300 dark:border-gray-700"
                          value={darkColor}
                          onChange={(e) => setDarkColor(e.target.value)}
                        />
                        <input
                          type="text"
                          className="flex-1 rounded-md border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                          value={darkColor}
                          onChange={(e) => setDarkColor(e.target.value)}
                          maxLength={7}
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="lightColor"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        {t('qrcode.lightColor', 'Light Color')}
                      </label>
                      <div className="flex items-center">
                        <input
                          type="color"
                          id="lightColor"
                          className="h-9 w-9 rounded-md mr-2 border border-gray-300 dark:border-gray-700"
                          value={lightColor}
                          onChange={(e) => setLightColor(e.target.value)}
                        />
                        <input
                          type="text"
                          className="flex-1 rounded-md border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                          value={lightColor}
                          onChange={(e) => setLightColor(e.target.value)}
                          maxLength={7}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    <div>
                      <label htmlFor="dotStyle" className="block text-sm font-medium text-gray-700 mb-1">Dot Style</label>
                      <select
                        id="dotStyle"
                        className="w-full rounded-md border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                        value={dotStyle}
                        onChange={(e) => setDotStyle(e.target.value as DotType)}
                      >
                        <option value="square">square</option>
                        <option value="rounded">rounded</option>
                        <option value="dots">dots</option>
                        <option value="classy">classy</option>
                        <option value="classy-rounded">classy-rounded</option>
                        <option value="extra-rounded">extra-rounded</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="eyeStyle" className="block text-sm font-medium text-gray-700 mb-1">Eye Style</label>
                      <select
                        id="eyeStyle"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                        value={eyeStyle}
                        onChange={(e) => setEyeStyle(e.target.value as CornerSquareType)}
                      >
                        <option value="square">square</option>
                        <option value="dot">dot</option>
                        <option value="extra-rounded">extra-rounded</option>
                        <option value="classy">classy</option>
                        <option value="classy-rounded">classy-rounded</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-900/30">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">{t('qrcode.gradient', 'Gradient')}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="gradientStart" className="block text-sm font-medium text-gray-700 mb-1">{t('qrcode.start', 'Start')}</label>
                        <input type="color" id="gradientStart" className="h-9 w-9 rounded-md border border-gray-300 dark:border-gray-700" value={gradientStart} onChange={(e) => setGradientStart(e.target.value)} />
                      </div>
                      <div>
                        <label htmlFor="gradientEnd" className="block text-sm font-medium text-gray-700 mb-1">{t('qrcode.end', 'End')}</label>
                        <input type="color" id="gradientEnd" className="h-9 w-9 rounded-md border border-gray-300 dark:border-gray-700" value={gradientEnd} onChange={(e) => setGradientEnd(e.target.value)} />
                      </div>
                    </div>
                    <div className="mt-3">
                      <label htmlFor="gradientAngle" className="block text-sm font-medium text-gray-700 mb-1">{t('qrcode.angle', 'Angle')} ({gradientAngle}°)</label>
                      <input type="range" id="gradientAngle" min="0" max="360" value={gradientAngle} onChange={(e) => setGradientAngle(parseInt(e.target.value))} className="w-full accent-blue-500" />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="logoUpload" className="block text-sm font-medium text-gray-700 mb-1">{t('qrcode.centerLogo', 'Center Logo')}</label>
                    <input type="file" id="logoUpload" accept="image/*" onChange={handleLogoUpload} className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 cursor-pointer" />
                  </div>
                  <div>
                    <label htmlFor="logoSize" className="block text-sm font-medium text-gray-700 mb-1">{t('qrcode.logoSize', 'Logo Size')} ({logoSize}%)</label>
                    <input type="range" id="logoSize" min="0" max="30" value={logoSize} onChange={(e) => setLogoSize(parseInt(e.target.value))} className="w-full accent-blue-500" />
                  </div>
                  </div>
                </div>
              </details>
            </div>
          </div>

          {/* QR Code Output Section */}
          <div className="order-1 lg:sticky lg:top-4">
            <div className="flex flex-col">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-lg font-semibold">{t('qrcode.preview', 'QR Code Preview')}</h2>
                <button
                  type="button"
                  onClick={handleCopyImage}
                  disabled={!qrCodeUrl}
                  className="px-3 py-1.5 text-sm rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  📋 {t('qrcode.buttons.copyImage', 'Copy image')}
                </button>
              </div>

              <div className="flex flex-col items-center justify-center p-3 rounded bg-gray-50 border border-gray-100 mb-3">
                {qrCodeUrl ? (
                  <>
                    <img
                      src={qrCodeUrl}
                      alt={`QR Code for: ${input}`}
                      className="mb-2 max-w-full"
                      style={{ maxHeight: `${size}px`, height: "auto" }}
                    />
                    <div className="text-xs text-center text-gray-500 break-all mt-1 px-2">
                      {input}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                    <svg
                      className="h-12 w-12 mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                      />
                    </svg>
                    <p>{t('qrcode.empty', 'Enter a URL or deeplink to generate QR code')}</p>
                  </div>
                )}
              </div>

              <div ref={qrRef} className="hidden" />
            </div>
          </div>
        </div>

        {/* Usage Tips */}
        <div className="mt-5 border-t border-gray-200 pt-4">
          <h2 className="text-lg font-semibold mb-3">{t('qrcode.tips', 'Deep‑Link Testing Tips')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-white p-3 rounded-md border border-gray-200">
              <div className="text-blue-500 mb-2">
                <svg
                  className="h-7 w-7"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="font-medium mb-2">App Deeplinks</h3>
              <p className="text-sm text-gray-600">
                For custom scheme deeplinks (like <code>myapp://</code>), scan
                the QR code with a phone that has the app installed.
              </p>
            </div>
            <div className="bg-white p-3 rounded-md border border-gray-200">
              <div className="text-blue-500 mb-2">
                <svg
                  className="h-7 w-7"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="font-medium mb-2">Share with Team</h3>
              <p className="text-sm text-gray-600">
                Use "Share Page with Link Pre-filled" to send a ready-to-use
                link to teammates with the QR already configured.
              </p>
            </div>
            <div className="bg-white p-3 rounded-md border border-gray-200">
              <div className="text-blue-500 mb-2">
                <svg
                  className="h-7 w-7"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
              </div>
              <h3 className="font-medium mb-2">Testing Best Practices</h3>
              <p className="text-sm text-gray-600">
                For regression testing, first test your deeplinks on a fresh app
                install before testing on your development build.
              </p>
            </div>
          </div>
        </div>

        {/* Related Debugging Tools */}
        <section className="mt-5 border-t border-gray-200 pt-4">
          <h2 className="text-lg font-semibold mb-3">
            Related Debugging Tools
          </h2>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <a
                href="/url-inspector"
                className="text-blue-600 hover:underline"
              >
                URL Parameter Inspector
              </a>
            </li>
            <li>
              <a
                href="/base64-decode"
                className="text-blue-600 hover:underline"
              >
                Text Base64 Decoder
              </a>
            </li>
          </ul>
        </section>

        {/* My Collection */}
        {savedQRCodes.length > 0 && (
          <div className="mt-8 border-t border-gray-200 pt-6">
            <h2 className="text-xl font-semibold mb-4">My Collection</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedQRCodes.map((code) => (
                <div
                  key={code.id}
                  className="bg-white p-4 rounded-md border border-gray-200"
                >
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-medium text-lg">{code.nickname}</h3>
                    <button
                      onClick={() => handleRemoveFromCollection(code.id)}
                      className="text-red-500 hover:text-red-600 transition"
                      title="Remove from collection"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                  <img
                    src={code.qrCodeUrl}
                    alt={`QR Code for: ${code.url}`}
                    className="mb-2 max-w-full"
                  />
                  <div className="text-xs text-gray-500 break-all mb-2">
                    {code.url}
                  </div>
                  <div className="flex items-center">
                    <button
                      onClick={() => handleLoadFromCollection(code)}
                      className="px-3 py-1 rounded-md bg-blue-500 hover:bg-blue-600 text-white text-sm transition"
                    >
                      Load
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default DeepLinkQRGenerator;
